import React, { useCallback, useEffect, useState } from 'react'
import {
  useEvent
} from '@emerald-works/react-event-bus-client'
import { useDispatch, useSelector } from 'react-redux'
import { useAlert } from '../../../../../hooks'
import { subscriptionSlice, userSlice } from '../../../../../reducers'
import { useRetention } from '../components/retention/hooks/useRetention'
import SuspendAlert from '../components/retention/components/suspend-alert'
import useAccountInfo from './useAccountInfo'

export const useSubscription = () => {
  const [suspension, setSuspension] = useState({})
  const [discount, setDiscount] = useState({})
  const dispatch = useDispatch()
  const { dispatch: localDispatch } = useRetention()
  const { enqueueAlert } = useAlert()
  const { accountInfo, accountInfoLoaded } = useAccountInfo()

  const profile = useSelector(userSlice.selectors.selectProfile)

  const subscriptionChangePending = useSelector(
    subscriptionSlice.selectors.selectSubscriptionChangePending
  )
  const subscriptionChangeReceived = useSelector(
    subscriptionSlice.selectors.selectSubscriptionChangeReceived
  )

  const [
    subscriptionSuspend,
    subscriptionCancel,
    subscriptionGetAccountInfo,
    subscriptionReactivate,
    subscriptionUpdate,
    subscriptionCreate,
    subscriptionResume,
    subscriptionRedeemCoupon
  ] = useEvent([
    subscriptionSlice.eventBus.subscriptionSuspend,
    subscriptionSlice.eventBus.subscriptionCancel,
    subscriptionSlice.eventBus.subscriptionGetAccountInfo,
    subscriptionSlice.eventBus.subscriptionReactivate,
    subscriptionSlice.eventBus.subscriptionUpdate,
    subscriptionSlice.eventBus.subscriptionCreate,
    subscriptionSlice.eventBus.subscriptionResume,
    subscriptionSlice.eventBus.subscriptionRedeemCoupon
  ])

  useEffect(() => {
    if ((subscriptionChangeReceived || !accountInfoLoaded) && profile?.username) {
      subscriptionGetAccountInfo.trigger({
        accountId: profile.username,
        invalidateCache: accountInfoLoaded
      })
    }
  }, [
    subscriptionChangeReceived,
    subscriptionGetAccountInfo,
    profile,
    accountInfoLoaded
  ])

  useEffect(() => {
    if (subscriptionChangeReceived) {
      const { suspend, expiredSubscription, redeem } = subscriptionChangeReceived

      if (suspend) {
        setSuspension({
          start: suspend.pausedAt,
          end: suspend.currentTermEndsAt
        })
        localDispatch({ type: 'suspendButton' })
        enqueueAlert(<SuspendAlert />)
        return
      }

      if (expiredSubscription) {
        localDispatch({ type: 'cancelButton' })
        return
      }

      if (redeem) {
        setDiscount({
          amount: redeem.coupon.discount.percent
        })
        localDispatch({ type: 'discountButton' })
      }
    }
  }, [subscriptionChangeReceived, enqueueAlert, localDispatch])

  const handleSubscription = useCallback(
    (event, params) => {
      if (!accountInfo) {
        console.log('*** no account info, no subscription calls')
        return
      }
      const data = {
        subscriptionId: accountInfo.subscriptionId,
        ...params
      }
      event.trigger(data)
      dispatch(subscriptionSlice.actions.setSubscriptionChangePending(data))
    },
    [dispatch, accountInfo]
  )

  const handleSuspend = useCallback(
    data => {
      handleSubscription(subscriptionSuspend, { reason: data.reason, comment: data.comment })
    },
    [handleSubscription, subscriptionSuspend]
  )

  const handleCancel = useCallback(
    data => {
      handleSubscription(subscriptionCancel, { reason: data.reason, comment: data.comment })
    },
    [handleSubscription, subscriptionCancel]
  )

  const handleReactivate = useCallback(() => {
    handleSubscription(subscriptionReactivate)
  },
  [handleSubscription, subscriptionReactivate]
  )

  const handleResume = useCallback(() => {
    handleSubscription(subscriptionResume)
  },
  [handleSubscription, subscriptionResume]
  )

  const handleCreate = useCallback(
    data => {
      handleSubscription(subscriptionCreate, {
        accountCode: data.accountCode,
        currency: data.currency,
        planCode: data.planCode
      })
    },
    [handleSubscription, subscriptionCreate]
  )

  const handleUpdate = useCallback(
    data => {
      handleSubscription(subscriptionUpdate, {
        currentPlan: data.currentPlan,
        code: data.code
      })
    },
    [handleSubscription, subscriptionUpdate]
  )

  const handleRedeemCoupon = useCallback(
    data => {
      handleSubscription(subscriptionRedeemCoupon, { accountId: profile.username, ...data })
    },
    [handleSubscription, subscriptionRedeemCoupon, profile]
  )

  return {
    accountInfoLoaded,
    accountInfo,
    handleSuspend,
    handleCancel,
    handleReactivate,
    handleResume,
    handleCreate,
    handleUpdate,
    handleRedeemCoupon,
    subscriptionChangePending,
    suspension,
    discount
  }
}
